import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import emailOutreachApi from './api/emailOutreachApi';
import MailboxManagerTab from './components/MailboxManagerTab';
import SequenceManagerTab from './components/SequenceManagerTab';
import ReplyTriageTab from './components/ReplyTriageTab';
import DeliverabilityTab from './components/DeliverabilityTab';
import TemplatesManagerTab from './components/TemplatesManagerTab';
import './EmailOutreachView.css';

function EmailOutreachView() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'mailboxes';
  const [activeTab, setActiveTab] = useState(initialTab);

  const [mailboxes, setMailboxes] = useState([]);
  const [sequences, setSequences] = useState([]);
  const [initialTemplateToApply, setInitialTemplateToApply] = useState(null);
  const [canManage, setCanManage] = useState(true);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  // Parse OAuth return params on mount
  useEffect(() => {
    const connectedAccount = searchParams.get('account');
    const isConnected = searchParams.get('mailbox_connected');
    const errorParam = searchParams.get('mailbox_error');

    if (isConnected) {
      setToast({
        type: 'success',
        message: `Mailbox successfully connected${connectedAccount ? `: ${connectedAccount}` : ''}.`,
      });
      // Clean query params
      setSearchParams({ tab: 'mailboxes' });
    } else if (errorParam) {
      setToast({
        type: 'error',
        message: `Failed to connect mailbox: ${errorParam.replace(/_/g, ' ')}.`,
      });
      setSearchParams({ tab: 'mailboxes' });
    }
  }, [searchParams, setSearchParams]);

  // Synchronize active tab with URL query parameter
  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);
    setSearchParams({ tab: tabKey });
  };

  const handleUseInSequence = (template) => {
    setInitialTemplateToApply(template);
    handleTabChange('sequences');
  };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [accessRes, mailboxesRes, sequencesRes] = await Promise.allSettled([
        emailOutreachApi.getAccess(),
        emailOutreachApi.getMailboxes(),
        emailOutreachApi.listSequences(),
      ]);

      if (accessRes.status === 'fulfilled' && accessRes.value?.success) {
        setCanManage(Boolean(accessRes.value.data?.canManage));
      }
      if (mailboxesRes.status === 'fulfilled' && mailboxesRes.value?.success) {
        setMailboxes(mailboxesRes.value.data || []);
      }
      if (sequencesRes.status === 'fulfilled' && sequencesRes.value?.success) {
        setSequences(sequencesRes.value.data || []);
      }
    } catch (err) {
      console.error('Failed to load email outreach data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  return (
    <div className="email-outreach-container">
      {/* Toast Notification */}
      {toast && (
        <div className={`eo-toast-banner ${toast.type}`}>
          <div className="eo-toast-body">
            <i
              className={
                toast.type === 'success' ? 'fas fa-check-circle' : 'fas fa-exclamation-circle'
              }
            ></i>
            <span>{toast.message}</span>
          </div>
          <button className="eo-toast-close" onClick={() => setToast(null)}>
            ×
          </button>
        </div>
      )}

      {/* Page Header */}
      <div className="eo-page-header">
        <div className="eo-header-info">
          <div className="eo-title-row">
            <span className="eo-module-tag">
              <i className="fas fa-paper-plane"></i> M07 Workspace
            </span>
            <h1 className="eo-main-title">Email Workspace & Automation</h1>
          </div>
          <p className="eo-header-desc">
            Manage connected Google & Microsoft mailboxes, build automated multi-step sequences,
            triage AI-classified replies, and safeguard sender reputation.
          </p>
        </div>

        <div className="eo-header-stats-strip">
          <div className="eo-header-kpi">
            <span className="eo-kpi-label">Connected Mailboxes</span>
            <strong className="eo-kpi-val">
              {mailboxes.filter((m) => m.status === 'connected').length}
            </strong>
          </div>
          <div className="eo-header-kpi">
            <span className="eo-kpi-label">Active Sequences</span>
            <strong className="eo-kpi-val">
              {sequences.filter((s) => s.status === 'active').length}
            </strong>
          </div>
          <div className="eo-header-kpi">
            <span className="eo-kpi-label">Active Enrolled Leads</span>
            <strong className="eo-kpi-val">
              {sequences.reduce((sum, s) => sum + (s.active_enrollments || 0), 0)}
            </strong>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="eo-tabs-bar">
        <button
          className={`eo-tab-btn ${activeTab === 'mailboxes' ? 'active' : ''}`}
          onClick={() => handleTabChange('mailboxes')}
        >
          <i className="fas fa-inbox"></i>
          <span>Mailboxes</span>
          {mailboxes.length > 0 && <span className="eo-tab-count">{mailboxes.length}</span>}
        </button>

        <button
          className={`eo-tab-btn ${activeTab === 'sequences' ? 'active' : ''}`}
          onClick={() => handleTabChange('sequences')}
        >
          <i className="fas fa-layer-group"></i>
          <span>Sequences</span>
          {sequences.length > 0 && <span className="eo-tab-count">{sequences.length}</span>}
        </button>

        <button
          className={`eo-tab-btn ${activeTab === 'replies' ? 'active' : ''}`}
          onClick={() => handleTabChange('replies')}
        >
          <i className="fas fa-comments"></i>
          <span>Reply Triage</span>
        </button>

        <button
          className={`eo-tab-btn ${activeTab === 'templates' ? 'active' : ''}`}
          onClick={() => handleTabChange('templates')}
        >
          <i className="fas fa-file-contract"></i>
          <span>Templates</span>
        </button>

        <button
          className={`eo-tab-btn ${activeTab === 'deliverability' ? 'active' : ''}`}
          onClick={() => handleTabChange('deliverability')}
        >
          <i className="fas fa-shield-alt"></i>
          <span>Deliverability</span>
        </button>
      </div>

      {/* Tab Panels */}
      <div className="eo-tab-panel">
        {activeTab === 'mailboxes' && (
          <MailboxManagerTab
            mailboxes={mailboxes}
            loading={loading}
            onRefresh={loadData}
            canManage={canManage}
          />
        )}

        {activeTab === 'sequences' && (
          <SequenceManagerTab
            sequences={sequences}
            mailboxes={mailboxes}
            loading={loading}
            onRefresh={loadData}
            canManage={canManage}
            initialTemplateToApply={initialTemplateToApply}
            onClearInitialTemplate={() => setInitialTemplateToApply(null)}
          />
        )}

        {activeTab === 'replies' && <ReplyTriageTab canManage={canManage} mailboxes={mailboxes} />}

        {activeTab === 'templates' && (
          <TemplatesManagerTab canManage={canManage} onUseInSequence={handleUseInSequence} />
        )}

        {activeTab === 'deliverability' && <DeliverabilityTab />}
      </div>
    </div>
  );
}

export default EmailOutreachView;
